import type { CustomerBudget } from '@clockwise/clients/customer-budgets';
import type { ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError, BUDGET_RESET_PERIOD_LABELS, totalAllocatedHours } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartNoAxesColumn, Pencil, Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RowActions } from '@/components/custom/row-actions';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageWindow } from '@/hooks/use-page-window';
import { customerBudgetsApi, serviceRolesApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { useIsAdministrator } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { cn, localTodayIso } from '@/lib/utils';
import { AsOfFilterToggle } from './as-of-filter-toggle';
import { ConfirmDialog } from './confirm-dialog';
import { CustomerBudgetActualsPanel } from './customer-budget-actuals-panel';
import { CustomerBudgetFormSheet } from './customer-budget-form-sheet';
import { PaginationFooter } from './pagination-footer';
import { RequirePermission } from './require-permission';

/** Server page size. A customer accumulates one budget per revision, so a
 *  long-lived account can have dozens — paged rather than fetched whole.
 *
 *  EXPORTED so the customer page's tab-count query can key on the same first
 *  page and share this panel's cache entry rather than issuing a second
 *  request. The sibling panels have the route re-declare their page size with
 *  a "mirrors X" comment; a shared constant cannot drift out of step. */
export const BUDGET_PAGE_SIZE = 25;

/** Matches the service-role autocomplete's preload cap. */
const ROLE_LIMIT = 500;

/** Column widths, in px, mirroring the Tailwind classes on the header cells:
 *  Effective (w-44) + Engagement (w-40) + Resets (w-32) + Total (w-24), one
 *  w-24 per role, and a floor for the actions column, which is the one that
 *  stretches. Kept in sync by hand — the table is `table-fixed`, so these
 *  numbers are what actually decides the layout. */
const FIXED_COLUMNS_WIDTH_PX = 176 + 160 + 128 + 96;
const ROLE_COLUMN_WIDTH_PX = 96;
const ACTIONS_MIN_WIDTH_PX = 96;

interface Props {
  customerId: string;
}

type SheetState = { mode: 'add' } | { mode: 'edit'; budget: CustomerBudget } | null;

/** The two readings of one customer's budgets: the agreement, and what has
 *  been spent against it. */
type View = 'budgets' | 'actuals';

/**
 * Customer-detail Budgets tab.
 *
 * A budget allocates hours across service roles, optionally scoped to one of
 * the customer's engagements — so several can be in force at once (one per
 * engagement, plus one attached to none). The table has one row per budget
 * and one column per service role, rather than a row per role.
 *
 * WHAT IT SHOWS IS THE AGREEMENT, not what has been spent against it. A period
 * of consumed-hours columns lived here and was taken out: it computed its own
 * allowances and its own variance, and disagreed with the Budget vs Actuals
 * report about the same customer in the same month. Spend now has exactly one
 * definition, the report's, and {@link CustomerBudgetActualsPanel} is where it
 * is read — behind the view switcher below, which is held back to
 * Administrator until the figures have been checked against the report on real
 * data.
 *
 * Toolbar: as-of date picker, "show past budgets" toggle, and Add gated on
 * `customer.budget.edit`. The switcher sits ABOVE that toolbar, because the
 * toolbar belongs to the Budgets view and not to both.
 */
export function CustomerBudgetsPanel({ customerId }: Props) {
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('customer.budget.edit');
  // A ROLE-NAME gate, not a permission, and deliberately temporary — see
  // `useIsAdministrator`. Everyone else sees this tab exactly as it was before
  // the actuals work: the agreement, and nothing derived from timesheets.
  const isAdministrator = useIsAdministrator();

  // Which of the tab's two views is on screen. Not in the URL: it is a look at
  // the same customer rather than a place, and the tab itself (`?tab=budgets`)
  // is what a link needs to carry.
  const [view, setView] = useState<View>('budgets');
  const showActuals = view === 'actuals';
  const [asOf, setAsOf] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [confirmDelete, setConfirmDelete] = useState<CustomerBudget | null>(null);

  useEffect(() => {
    setPage(0);
  }, [asOf, showAll]);

  const offset = page * BUDGET_PAGE_SIZE;
  // `showAll` drops the as-of filter entirely; otherwise snapshot on the
  // chosen date, defaulting to today.
  const effectiveAsOf = showAll ? undefined : asOf || localTodayIso();

  const query = useQuery({
    queryKey: queryKeys.customerBudgets.list(customerId, {
      asOf: effectiveAsOf,
      limit: BUDGET_PAGE_SIZE,
      offset,
    }),
    queryFn: () =>
      customerBudgetsApi.list(customerId, {
        asOf: effectiveAsOf,
        limit: BUDGET_PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  // Role names for the column headers. The budget row carries ids only —
  // the map is keyed by service-role id so a rename can't orphan it — so the
  // names are resolved here from the tenant catalogue.
  const rolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list({ limit: ROLE_LIMIT }),
    queryFn: () => serviceRolesApi.list({ limit: ROLE_LIMIT }),
    staleTime: 5 * 60 * 1000,
  });
  const roleNameById = new Map(
    (rolesQuery.data?.data ?? []).map((r: ServiceRole) => [r.id, r.name]),
  );

  /**
   * Label for one role column. Three distinct states, because collapsing
   * them lies to the reader (G15):
   *   - in flight        → '…'
   *   - names failed     → the raw id, plus a banner above; "Unknown role" here
   *                        would claim every role was deleted when in fact the
   *                        lookup never arrived
   *   - settled, missing → 'Unknown role', a genuine data gap
   */
  const roleLabel = (roleId: string): string => {
    if (rolesQuery.isPending) return '…';
    if (rolesQuery.isError) return roleId;
    return roleNameById.get(roleId) ?? 'Unknown role';
  };

  const budgets = query.data?.data ?? [];

  // One column per service role that any budget on this page allocates to —
  // reading hours down a column beats scanning chips across rows. Sorted by
  // label (not by the order the roles happen to come back in) so the columns
  // don't reshuffle between pages.
  const roleColumns = Array.from(new Set(budgets.flatMap((b) => Object.keys(b.allocations)))).sort(
    (a, b) => roleLabel(a).localeCompare(roleLabel(b)),
  );

  // `table-fixed` divides the available width, so past some role count the
  // hour columns would be squeezed below legibility. Floor the table at the
  // sum of its fixed columns and let the shadcn wrapper scroll instead.
  const TABLE_MIN_WIDTH_PX =
    FIXED_COLUMNS_WIDTH_PX + roleColumns.length * ROLE_COLUMN_WIDTH_PX + ACTIONS_MIN_WIDTH_PX;

  const total = query.data?.meta?.total ?? 0;
  const { pageCount, shownOffset } = usePageWindow({
    page,
    setPage,
    pageSize: BUDGET_PAGE_SIZE,
    total,
    responseOffset: query.data?.meta?.offset,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerBudgetsApi.delete(id),
    onSuccess: () => {
      toast.success('Budget deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerBudgets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      setConfirmDelete(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete budget'),
  });

  return (
    <div className="space-y-3">
      {/* TWO PEER VIEWS, so a two-way switch rather than a button that turns
          one of them on. A pressed toggle states where you are and leaves the
          way back to be inferred from its own fill; a pair of tabs names both
          destinations at once, and the one you are not on is the way back.
          Same blue-chip segmented toggle as every other two-up switch in the
          app (Approvals' view switch, Timesheets' week/day switch, the
          unlock form's date-mode switch) — not the raw shadcn `Tabs`, whose
          active state is a cream fill, not this app's blue+white "selected"
          look. */}
      {isAdministrator ? (
        <div
          data-surface="panel"
          data-slot="toggle-group"
          role="tablist"
          className="inline-flex items-center gap-0 rounded-md border light:bg-card p-0.5"
        >
          <ViewToggleButton active={view === 'budgets'} onClick={() => setView('budgets')}>
            Budgets
          </ViewToggleButton>
          <ViewToggleButton active={view === 'actuals'} onClick={() => setView('actuals')}>
            <ChartNoAxesColumn className="size-4" />
            Budget vs Actuals
          </ViewToggleButton>
        </div>
      ) : null}

      {/* THE BUDGETS VIEW'S OWN TOOLBAR, and it renders only for that view.
          The as-of controls pick which AGREEMENTS are in force, a question the
          actuals view does not ask — it has a reporting period of its own, and
          two date controls side by side meaning different things is how a
          reader ends up mistrusting both. Adding a budget is an edit to the
          agreement, which is not what the other view is showing. */}
      {showActuals ? null : (
        <div className="flex flex-wrap items-center gap-3">
          <AsOfFilterToggle
            asOf={asOf}
            onAsOfChange={setAsOf}
            showAll={showAll}
            onShowAllChange={setShowAll}
            showAllLabel="Show past budgets"
            statusText={
              showAll
                ? 'Showing every budget, past and present.'
                : asOf
                  ? `Showing the budgets in force on ${asOf}.`
                  : 'Showing the budgets in force today.'
            }
          />
          <div className="ml-auto">
            <RequirePermission permission="customer.budget.edit">
              <Button size="sm" onClick={() => setSheet({ mode: 'add' })}>
                <Plus className="size-4" />
                Add budget
              </Button>
            </RequirePermission>
          </div>
        </div>
      )}

      {showActuals ? (
        /* The report's answer, laid out for a customer page. It runs its own
           query and owns its own period, so nothing above it is passed down. */
        <CustomerBudgetActualsPanel customerId={customerId} />
      ) : (
        <>
          {/* Budgets loaded but their role NAMES didn't. The hours are still
            correct and worth showing, so this is a banner rather than an error
            state that hides them. */}
          {rolesQuery.isError ? (
            <p className="rounded-md border border-destructive/50 p-2 text-xs text-destructive">
              Couldn’t load service-role names, so roles below are shown by id.
            </p>
          ) : null}

          <div className="rounded-md border">
            {query.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : query.isError ? (
              isForbidden(query.error) ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  You don’t have access to this customer’s budgets.
                </p>
              ) : (
                <p className="p-4 text-sm text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load budgets.'}
                </p>
              )
            ) : budgets.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {showAll
                  ? 'No budgets on record.'
                  : 'No budgets in force on this date. Past or future budgets may exist — tick “Show past budgets”.'}
              </p>
            ) : (
              <Table className="table-fixed" style={{ minWidth: TABLE_MIN_WIDTH_PX }}>
                {/* `bg-surface-subtle` — the same cream header tint every
                    other list table uses (see `sticky-table-scroll.tsx`);
                    this table isn't wrapped in that component (it scrolls
                    with the page, not internally), so the tint has to be
                    stated here instead. */}
                <TableHeader className="bg-surface-subtle">
                  <TableRow>
                    <TableHead className="w-44">Effective</TableHead>
                    <TableHead className="w-40">Engagement</TableHead>
                    <TableHead className="w-32">Resets</TableHead>
                    {roleColumns.map((roleId) => (
                      <TableHead
                        key={roleId}
                        className="w-24 truncate px-2 text-xs"
                        title={roleLabel(roleId)}
                      >
                        {roleLabel(roleId)}
                      </TableHead>
                    ))}
                    <TableHead className="w-24 px-2">Total</TableHead>
                    {/* The only column without a width, so it soaks up whatever
                      the container has spare — that keeps the hour columns
                      packed against Resets instead of drifting right. Its
                      buttons stay pinned to the far edge. */}
                    <TableHead className="text-right" aria-label="actions" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgets.map((b) => {
                    const ended = b.effectiveTo != null && b.effectiveTo <= localTodayIso();
                    return (
                      <TableRow key={b.id}>
                        <TableCell className="text-xs">
                          {b.effectiveFrom} →{' '}
                          <span className={ended ? 'text-muted-foreground' : 'font-medium'}>
                            {b.effectiveTo ?? 'Ongoing'}
                          </span>
                        </TableCell>
                        <TableCell
                          className="truncate text-sm"
                          title={b.engagementName ?? undefined}
                        >
                          {/* Three states (G15): a name, "no engagement" (the dash),
                            and an id that no longer resolves — a real data gap
                            that must not read as either of the other two. */}
                          {b.engagementName ?? (
                            <span
                              className="text-muted-foreground"
                              title={b.engagementId ?? undefined}
                            >
                              {b.engagementId ? 'Unknown engagement' : '—'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {BUDGET_RESET_PERIOD_LABELS[b.resetPeriod]}
                        </TableCell>
                        {roleColumns.map((roleId) => {
                          const hours = b.allocations[roleId];
                          return (
                            <TableCell key={roleId} className="px-2 text-sm tabular-nums">
                              {/* An unallocated role is a real zero-hours gap, not
                                a missing value — a dash reads as "none" without
                                competing with the numbers that are there. */}
                              {hours == null ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                `${hours}h`
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="px-2 text-sm font-medium tabular-nums">
                          {totalAllocatedHours(b.allocations)}h
                        </TableCell>
                        <TableCell className="text-right">
                          {canEdit ? (
                            <RowActions>
                              <Button
                                size="icon-sm"
                                variant="ghost"
                                aria-label="Edit budget"
                                onClick={() => setSheet({ mode: 'edit', budget: b })}
                              >
                                <Pencil className="size-4" />
                              </Button>
                              <Button
                                size="icon-sm"
                                variant="ghost"
                                aria-label="Delete budget"
                                onClick={() => setConfirmDelete(b)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </RowActions>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          <PaginationFooter
            shownOffset={shownOffset}
            shownCount={budgets.length}
            total={total}
            pageSize={BUDGET_PAGE_SIZE}
            page={page}
            pageCount={pageCount}
            isFetching={query.isFetching}
            onPrevious={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}

      <CustomerBudgetFormSheet
        open={sheet?.mode === 'add' || sheet?.mode === 'edit'}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        customerId={customerId}
        budget={sheet?.mode === 'edit' ? sheet.budget : null}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        onCancel={() => setConfirmDelete(null)}
        title="Delete this budget?"
        // Delete is for a budget that should never have existed. Ending one is
        // an edit that sets Effective to — say so, or people will delete
        // history they meant to close.
        description="This removes the budget outright and cannot be undone. To stop a budget applying from a date onward, edit it and set “Effective to” instead."
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        confirmDisabled={deleteMutation.isPending}
        destructive
        onConfirm={() => {
          if (confirmDelete && !deleteMutation.isPending) deleteMutation.mutate(confirmDelete.id);
        }}
      />
    </div>
  );
}

/** Matches Approvals' `ViewButton` / Timesheets' view-switch chip for the
 *  blue-chip segmented LOOK, not shadcn Tabs' default styling — but Budgets
 *  and Budget vs Actuals are still two peer views (see the caller's own
 *  comment), so this carries `role="tab"`/`aria-selected`, not the toggle's
 *  `aria-pressed`. */
function ViewToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      data-slot="toggle-group-item"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors',
        active
          ? 'bg-muted text-foreground light:bg-primary light:text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
